import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { GridApi, RowDoubleClickedEvent, ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { useAction, useMutation, useQuery } from 'convex/react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  Loader2,
  AlertOctagon,
  HelpCircle,
  ExternalLink,
  EyeOff,
  Eye,
  ListTodo,
  Play,
  FileX2,
  Filter,
  Copy,
  Check,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { CreateTaskDialog } from '@/components/create-task-dialog';
import { RecentDealLink } from '@/components/recent-deal-link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGrid } from '@/contexts/grid-context';
import { useRecentDealByDomain } from '@/hooks/use-deals';
import { cn } from '@/lib/utils';
import { api } from '../../../../convex/_generated/api';

// Domain row for AG Grid (domain-centric view)
interface FdicDomainRow {
  domain: string;
  cert: string;
  fdicId?: string; // FDIC institution ID for direct links
  status: 'active' | 'inactive' | 'not_found' | 'invalid' | 'error' | 'unknown';
  institutionName?: string;
  city?: string;
  state?: string;
  lastChecked?: number;
  isSuppressed?: boolean;
  suppressReason?: string;
}

// Row for domains without any license
interface NoLicenseRow {
  domain: string;
  orgName?: string;
  city?: string;
  state?: string;
  country?: string;
}

// Status badge renderer
function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'active': {
      return (
        <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700">
          <CheckCircle2 className="size-3" />
          Active
        </Badge>
      );
    }
    case 'inactive': {
      return (
        <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700">
          <AlertTriangle className="size-3" />
          Inactive
        </Badge>
      );
    }
    case 'not_found': {
      return (
        <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700">
          <XCircle className="size-3" />
          Not Found
        </Badge>
      );
    }
    case 'invalid': {
      return (
        <Badge variant="outline" className="gap-1 border-purple-200 bg-purple-50 text-purple-700">
          <AlertOctagon className="size-3" />
          Invalid Format
        </Badge>
      );
    }
    case 'error': {
      return (
        <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700">
          <XCircle className="size-3" />
          Error
        </Badge>
      );
    }
    default: {
      return (
        <Badge variant="outline" className="gap-1">
          <HelpCircle className="size-3" />
          Unknown
        </Badge>
      );
    }
  }
}

// ============================================================================
// Status Filter Configuration (chip-based header, matches Monitor Events page)
// ============================================================================

type FdicStatus = 'active' | 'inactive' | 'not_found' | 'invalid' | 'error';

const FDIC_STATUS_ORDER: FdicStatus[] = ['active', 'inactive', 'not_found', 'invalid', 'error'];

const FDIC_STATUS_CONFIG: Record<FdicStatus, {
  label: string;
  icon: React.ElementType;
  className: string;
  description: string;
}> = {
  active: {
    label: 'Active',
    icon: CheckCircle2,
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
    description: 'Valid & active FDIC certificate',
  },
  inactive: {
    label: 'Inactive',
    icon: AlertTriangle,
    className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
    description: 'Institution no longer active',
  },
  not_found: {
    label: 'Not Found',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 hover:bg-red-100',
    description: 'Not in FDIC database',
  },
  invalid: {
    label: 'Invalid Format',
    icon: AlertOctagon,
    className: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
    description: 'Invalid cert number',
  },
  error: {
    label: 'API Errors',
    icon: XCircle,
    className: 'bg-rose-100 text-rose-800 hover:bg-rose-100',
    description: 'Could not verify',
  },
};

// Contextual explainer banners shown when a status filter is selected
const STATUS_BANNERS: Partial<Record<FdicStatus, {
  cardClass: string;
  textClass: string;
  title: string;
  description: string;
}>> = {
  inactive: {
    cardClass: 'border-amber-200 bg-amber-50',
    textClass: 'text-amber-800',
    title: 'Inactive Institutions:',
    description: 'These domains have FDIC certificates for institutions that are no longer active.',
  },
  not_found: {
    cardClass: 'border-red-200 bg-red-50',
    textClass: 'text-red-800',
    title: 'Not Found:',
    description: 'These certificate numbers were not found in FDIC\'s database. The cert number may be incorrect.',
  },
  invalid: {
    cardClass: 'border-purple-200 bg-purple-50',
    textClass: 'text-purple-800',
    title: 'Invalid Format:',
    description: 'These certificate numbers are not valid integers. FDIC certificate numbers should be numeric only.',
  },
  error: {
    cardClass: 'border-red-200 bg-red-50',
    textClass: 'text-red-800',
    title: 'API Errors:',
    description: 'These certificates could not be verified due to FDIC API errors. They will be retried on the next run.',
  },
};

// Toggle a value in/out of a selection array
function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// Helper to build FDIC URL - uses ID for direct link when available
function getFdicUrl(cert: string, fdicId?: string): string {
  if (fdicId) {
    return `https://banks.data.fdic.gov/bankfind-suite/bankfind/details/${fdicId}`;
  }
  // Fallback to search by cert number if no ID available
  return `https://banks.data.fdic.gov/bankfind-suite/bankfind?activeStatus=0%20OR%201&branchOffices=true&cert=${cert}&pageNumber=1&resultLimit=25&searchPush=true&sortField=NAME&sortOrder=ASC`;
}

// Function to create column definitions for FDIC domains grid (domain-centric)
function createFdicColumnDefs(
  onCreateTask: (domain: string, status: string) => void,
  getRecentDeal: (domain: string) => Promise<{ id: string } | null>,
  onSuppress: (domain: string, cert: string) => void,
  onUnsuppress: (domain: string, cert: string) => void,
): ColDef<FdicDomainRow>[] {
  return [
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 0,
      minWidth: 180,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<FdicDomainRow>) => (
        <Link
          to={`/app/domains/${params.value}`}
          className="text-primary hover:underline"
        >
          {params.value}
        </Link>
      ),
    },
    {
      headerName: 'Deal',
      flex: 0,
      minWidth: 90,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<FdicDomainRow>) => {
        if (!params.data)
          return null;
        return <RecentDealLink domain={params.data.domain} getRecentDeal={getRecentDeal} />;
      },
    },
    {
      field: 'cert',
      headerName: 'FDIC Cert #',
      flex: 0,
      minWidth: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<FdicDomainRow>) => (
        <a
          href={getFdicUrl(params.value, params.data?.fdicId)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {params.value}
          <ExternalLink className="ml-1 inline-block size-3" />
        </a>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0,
      minWidth: 120,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<FdicDomainRow>) => (
        <StatusBadge status={params.value} />
      ),
    },
    {
      field: 'institutionName',
      headerName: 'Institution Name',
      flex: 0,
      minWidth: 200,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 0,
      minWidth: 120,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0,
      minWidth: 70,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'lastChecked',
      headerName: 'Last Checked',
      flex: 0,
      minWidth: 130,
      sortable: true,
      valueFormatter: (params) => {
        if (!params.value)
          return '—';
        return formatDistanceToNow(new Date(params.value), { addSuffix: true });
      },
    },
    {
      headerName: 'Task',
      flex: 0,
      minWidth: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<FdicDomainRow>) => {
        if (!params.data)
          return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onCreateTask(params.data!.domain, `FDIC ${params.data!.status}`);
            }}
          >
            <ListTodo className="size-4" />
            <span className="text-xs">Add</span>
          </Button>
        );
      },
    },
    {
      headerName: '',
      flex: 0,
      width: 50,
      minWidth: 50,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<FdicDomainRow>) => {
        if (!params.data)
          return null;
        const { domain, cert, isSuppressed } = params.data;

        return (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={(e) => {
              e.stopPropagation();
              if (isSuppressed) {
                onUnsuppress(domain, cert);
              } else {
                onSuppress(domain, cert);
              }
            }}
            title={isSuppressed ? 'Unhide this record' : 'Hide this record'}
          >
            {isSuppressed ? <Eye className="size-4" /> : <EyeOff className="size-4 text-muted-foreground" />}
          </Button>
        );
      },
    },
  ];
}

// Column definitions for "no license" table
function createNoLicenseColumnDefs(
  onCreateTask: (domain: string, status: string) => void,
  getRecentDeal: (domain: string) => Promise<{ id: string } | null>,
): ColDef<NoLicenseRow>[] {
  return [
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 0,
      minWidth: 180,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<NoLicenseRow>) => (
        <Link
          to={`/app/domains/${params.value}#licenses`}
          className="text-primary hover:underline"
        >
          {params.value}
        </Link>
      ),
    },
    {
      headerName: 'Deal',
      flex: 0,
      minWidth: 90,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<NoLicenseRow>) => {
        if (!params.data)
          return null;
        return <RecentDealLink domain={params.data.domain} getRecentDeal={getRecentDeal} />;
      },
    },
    {
      field: 'orgName',
      headerName: 'Organization',
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 0,
      minWidth: 120,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0,
      minWidth: 70,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 0,
      minWidth: 90,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      headerName: 'Task',
      flex: 0,
      minWidth: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<NoLicenseRow>) => {
        if (!params.data)
          return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onCreateTask(params.data!.domain, 'No License');
            }}
          >
            <ListTodo className="size-4" />
            <span className="text-xs">Add</span>
          </Button>
        );
      },
    },
  ];
}

function FdicMonitorPage() {
  const [selectedStatuses, setSelectedStatuses] = useState<FdicStatus[]>([]);
  const [showNoLicense, setShowNoLicense] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [copiedCount, setCopiedCount] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogData, setTaskDialogData] = useState<{ domain: string; status: string } | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const gridApiRef = useRef<GridApi<FdicDomainRow> | null>(null);
  const { registerGridApi } = useGrid();
  const navigate = useNavigate();
  const { setLabel, removeLabel } = useBreadcrumb();
  const { getRecentDeal } = useRecentDealByDomain();

  // Set custom breadcrumb label
  useEffect(() => {
    const path = '/app/fdic-monitor';
    setLabel(path, { name: 'FDIC Monitor', path });
    return () => removeLabel(path);
  }, [setLabel, removeLabel]);

  // Query Convex for FDIC data
  const fdicData = useQuery(api.fdic.getFdicPageData);
  const noLicenseCount = useQuery(api.fdic.getDomainsWithoutLicenseCount);
  // Only fetch no-license domains when that view is active
  const noLicenseData = useQuery(
    api.fdic.getDomainsWithoutLicense,
    showNoLicense ? {} : 'skip',
  );
  const suppressRecord = useMutation(api.fdic.suppressRecord);
  const unsuppressRecord = useMutation(api.fdic.unsuppressRecord);
  const runFdicMonitor = useAction(api.fdicAction.runFdicMonitor);

  const handleRunScan = useCallback(async () => {
    setIsScanning(true);
    try {
      // The action is typed `unknown`; narrow to the shape we care about
      const result = await runFdicMonitor({ force: true }) as { skipped?: boolean } | null | undefined;
      if (result?.skipped) {
        toast.info('Scan already completed today');
      } else {
        toast.success('FDIC scan started successfully');
      }
    } catch (error) {
      console.error('Failed to start FDIC scan:', error);
      toast.error('Failed to start FDIC scan');
    } finally {
      setIsScanning(false);
    }
  }, [runFdicMonitor]);

  const isLoading = fdicData === undefined;

  // Build suppressed set for quick lookup
  const suppressedSet = useMemo(() => {
    return new Set(fdicData?.suppressedSet || []);
  }, [fdicData?.suppressedSet]);

  // Get suppression reason for a domain-cert pair
  const getSuppressionReason = useCallback((domain: string, cert: string) => {
    const record = fdicData?.suppressed?.find((s) => s.domain === domain && s.cert === cert);
    return record?.reason;
  }, [fdicData?.suppressed]);

  const handleSuppress = useCallback(async (domain: string, cert: string) => {
    try {
      await suppressRecord({ domain, cert });
      toast.success(`Hidden ${domain} (cert: ${cert})`);
    } catch {
      toast.error('Failed to hide record');
    }
  }, [suppressRecord]);

  const handleUnsuppress = useCallback(async (domain: string, cert: string) => {
    try {
      await unsuppressRecord({ domain, cert });
      toast.success(`Unhidden ${domain} (cert: ${cert})`);
    } catch {
      toast.error('Failed to unhide record');
    }
  }, [unsuppressRecord]);

  const handleOpenTaskDialog = useCallback((domain: string, status: string) => {
    setTaskDialogData({ domain, status });
    setTaskDialogOpen(true);
  }, []);

  // Build all rows from domains (domain-centric)
  const allRows = useMemo(() => {
    if (!fdicData?.domains)
      return [];

    // Build cert info map for quick lookup
    const certInfoMap = new Map<string, {
      status: string;
      institutionName?: string;
      city?: string;
      state?: string;
      lastChecked: number;
      fdicId?: string;
    }>();

    if (fdicData.certificates) {
      for (const cert of fdicData.certificates) {
        certInfoMap.set(cert.cert, {
          status: cert.status,
          institutionName: cert.institutionName ?? undefined,
          city: cert.city ?? undefined,
          state: cert.state ?? undefined,
          lastChecked: cert.lastChecked,
          fdicId: cert.fdicId,
        });
      }
    }

    const rows: FdicDomainRow[] = [];
    for (const domainEntry of fdicData.domains) {
      if (!domainEntry.certs)
        continue;

      for (const certInfo of domainEntry.certs) {
        const fullCertInfo = certInfoMap.get(certInfo.cert);
        const status = (fullCertInfo?.status || certInfo.status || 'unknown') as FdicDomainRow['status'];
        const suppKey = `${domainEntry.domain}|${certInfo.cert}`;
        const isSuppressed = suppressedSet.has(suppKey);

        rows.push({
          domain: domainEntry.domain,
          cert: certInfo.cert,
          fdicId: fullCertInfo?.fdicId,
          status,
          institutionName: fullCertInfo?.institutionName || certInfo.institutionName,
          city: fullCertInfo?.city,
          state: fullCertInfo?.state,
          lastChecked: fullCertInfo?.lastChecked,
          isSuppressed,
          suppressReason: isSuppressed ? getSuppressionReason(domainEntry.domain, certInfo.cert) : undefined,
        });
      }
    }

    return rows;
  }, [fdicData, suppressedSet, getSuppressionReason]);

  // Rows respecting the suppression visibility toggle
  const rowsBase = useMemo(
    () => (showSuppressed ? allRows : allRows.filter((r) => !r.isSuppressed)),
    [allRows, showSuppressed],
  );

  // Facet counts derived from the visible set (independent of status selection)
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of rowsBase) {
      counts[row.status] = (counts[row.status] || 0) + 1;
    }
    return counts;
  }, [rowsBase]);

  const suppressedCount = useMemo(
    () => allRows.filter((r) => r.isSuppressed).length,
    [allRows],
  );

  const noLicenseTotal = noLicenseCount?.count ?? 0;

  // Apply the status selection on top of the visible set
  const filteredRows = useMemo(() => {
    if (selectedStatuses.length === 0)
      return rowsBase;
    const statuses = new Set<string>(selectedStatuses);
    return rowsBase.filter((r) => statuses.has(r.status));
  }, [rowsBase, selectedStatuses]);

  // Create column definitions with action handlers
  const fdicColumnDefs = useMemo(
    () => createFdicColumnDefs(handleOpenTaskDialog, getRecentDeal, handleSuppress, handleUnsuppress),
    [handleOpenTaskDialog, getRecentDeal, handleSuppress, handleUnsuppress],
  );

  const noLicenseColumnDefs = useMemo(
    () => createNoLicenseColumnDefs(handleOpenTaskDialog, getRecentDeal),
    [handleOpenTaskDialog, getRecentDeal],
  );

  // Domains without any license (from separate query)
  const domainsWithoutLicense = useMemo(() => {
    return noLicenseData?.domains ?? [];
  }, [noLicenseData?.domains]);

  // Facet chip handlers
  const toggleStatus = useCallback((status: FdicStatus) => {
    setShowNoLicense(false);
    setSelectedStatuses((prev) => toggleValue(prev, status));
  }, []);

  const handleShowAll = useCallback(() => {
    setShowNoLicense(false);
    setSelectedStatuses([]);
  }, []);

  const toggleNoLicense = useCallback(() => {
    setSelectedStatuses([]);
    setShowNoLicense((prev) => !prev);
  }, []);

  const currentCount = showNoLicense ? domainsWithoutLicense.length : filteredRows.length;

  const handleCopyCount = useCallback(() => {
    void navigator.clipboard.writeText(String(currentCount));
    setCopiedCount(true);
    setTimeout(() => setCopiedCount(false), 2000);
  }, [currentCount]);

  // Export the currently filtered rows to an Excel spreadsheet
  const handleExportReport = useCallback(() => {
    if (currentCount === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const wb = utils.book_new();
      const timestamp = format(new Date(), 'yyyyMMdd_HHmm');

      if (showNoLicense) {
        const exportRows = domainsWithoutLicense.map((row) => ({
          'Domain': row.domain,
          'Organization': row.orgName ?? '',
          'City': row.city ?? '',
          'State': row.state ?? '',
          'Country': row.country ?? '',
        }));

        const ws = utils.json_to_sheet(exportRows);
        ws['!cols'] = [
          { wch: 24 }, // Domain
          { wch: 32 }, // Organization
          { wch: 16 }, // City
          { wch: 8 }, // State
          { wch: 10 }, // Country
        ];
        utils.book_append_sheet(wb, ws, 'No License');

        const filename = `fdic_no_license_${timestamp}.xlsx`;
        writeFile(wb, filename);
        toast.success(`Exported ${exportRows.length} domains to ${filename}`);
      } else {
        const exportRows = filteredRows.map((row) => ({
          'Domain': row.domain,
          'FDIC Cert #': row.cert,
          'Status': FDIC_STATUS_CONFIG[row.status as FdicStatus]?.label ?? row.status,
          'Institution Name': row.institutionName ?? '',
          'City': row.city ?? '',
          'State': row.state ?? '',
          'Last Checked': row.lastChecked ? format(new Date(row.lastChecked), 'MM/dd/yyyy h:mm a') : '',
          'Hidden': row.isSuppressed ? 'Yes' : 'No',
        }));

        const ws = utils.json_to_sheet(exportRows);
        ws['!cols'] = [
          { wch: 24 }, // Domain
          { wch: 12 }, // FDIC Cert #
          { wch: 14 }, // Status
          { wch: 32 }, // Institution Name
          { wch: 16 }, // City
          { wch: 8 }, // State
          { wch: 18 }, // Last Checked
          { wch: 8 }, // Hidden
        ];
        utils.book_append_sheet(wb, ws, 'FDIC Monitor');

        const statusLabel = selectedStatuses.length > 0
          ? selectedStatuses.join('_')
          : 'all';
        const filename = `fdic_monitor_${statusLabel}_${timestamp}.xlsx`;
        writeFile(wb, filename);
        toast.success(`Exported ${exportRows.length} rows to ${filename}`);
      }
    } catch {
      toast.error('Failed to export report');
    }
  }, [currentCount, showNoLicense, domainsWithoutLicense, filteredRows, selectedStatuses]);

  const handleRowDoubleClick = (event: RowDoubleClickedEvent<FdicDomainRow>) => {
    if (event.data) {
      navigate(`/app/domains/${event.data.domain}#licenses`);
    }
  };

  const handleGridReady = (params: { api: GridApi<FdicDomainRow> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatuses([]);
    setShowNoLicense(false);
    setShowSuppressed(false);
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
    }
  };

  const hasActiveFilters
    = selectedStatuses.length > 0
      || showNoLicense
      || showSuppressed
      || searchQuery.trim() !== '';

  return (
    <Container className="pb-12">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">FDIC License Monitor</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportReport}
            disabled={isLoading || currentCount === 0}
            className="gap-2"
          >
            <FileSpreadsheet className="size-4" />
            Export
            {' '}
            {currentCount.toLocaleString()}
            {' '}
            {showNoLicense ? 'domains' : 'rows'}
          </Button>
          <Button
            onClick={handleRunScan}
            disabled={isScanning}
            className="gap-2"
          >
            {isScanning
              ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Running Scan...
                  </>
                )
              : (
                  <>
                    <Play className="size-4" />
                    Run Scan
                  </>
                )}
          </Button>
        </div>
      </div>

      {fdicData?.lastRun && (
        <div className="mb-4 text-sm text-muted-foreground">
          Last check:
          {' '}
          {fdicData.lastRun.date}
          {fdicData.lastRun.completedAt && (
            <>
              {' '}
              (completed
              {' '}
              {format(new Date(fdicData.lastRun.completedAt), 'PPP p')}
              )
            </>
          )}
          {fdicData.lastRun.durationMs && (
            <span className="ml-2 text-muted-foreground/70">
              •
              {' '}
              {(fdicData.lastRun.durationMs / 1000).toFixed(1)}
              s
            </span>
          )}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <Card className="mb-6">
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span>Loading FDIC data...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {!isLoading && (
        <Card className="mb-6 border-dashed">
          <CardContent className="py-4">
            <div className="flex flex-col gap-4">
              {/* Filter groups */}
              <div className="flex flex-wrap gap-x-8 gap-y-4">
                {/* Status */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Status
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={handleShowAll}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                        selectedStatuses.length === 0 && !showNoLicense
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                      )}
                    >
                      All
                      <span className="tabular-nums opacity-70">{rowsBase.length}</span>
                    </button>
                    {FDIC_STATUS_ORDER.map((status) => {
                      const config = FDIC_STATUS_CONFIG[status];
                      const Icon = config.icon;
                      const isSelected = selectedStatuses.includes(status);
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => toggleStatus(status)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium transition-all',
                            config.className,
                            isSelected
                              ? 'ring-2 ring-primary/50 ring-offset-1'
                              : 'opacity-60 hover:opacity-100',
                          )}
                          title={config.description}
                        >
                          <Icon className="size-3" />
                          {config.label}
                          <span className="tabular-nums opacity-70">{statusCounts[status] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Licenses */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Licenses
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={toggleNoLicense}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium transition-all',
                        'bg-orange-100 text-orange-800 hover:bg-orange-100',
                        showNoLicense
                          ? 'ring-2 ring-primary/50 ring-offset-1'
                          : 'opacity-60 hover:opacity-100',
                      )}
                      title="Non-deleted domains without any license on file"
                    >
                      <FileX2 className="size-3" />
                      No License
                      <span className="tabular-nums opacity-70">{noLicenseTotal}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Controls bar */}
              <div className="flex items-center justify-between gap-3 border-t pt-3">
                <div className="flex items-center gap-2">
                  {suppressedCount > 0 && (
                    <Button
                      variant={showSuppressed ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => setShowSuppressed(!showSuppressed)}
                      className="h-7 gap-1.5 text-xs"
                    >
                      {showSuppressed ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                      {showSuppressed ? 'Showing hidden' : 'Hidden'}
                      {' '}
                      (
                      {suppressedCount}
                      )
                    </Button>
                  )}
                  {hasActiveFilters
                    ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFilters}
                          className="gap-1.5 text-xs text-muted-foreground"
                        >
                          <RotateCcw className="size-3" />
                          Clear filters
                        </Button>
                      )
                    : (
                        <span className="text-xs text-muted-foreground">
                          Click a status to filter, or search below
                        </span>
                      )}
                </div>

                <div className="flex items-center gap-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleCopyCount}
                          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                        >
                          {copiedCount
                            ? (
                                <Check className="size-3.5 text-green-600" />
                              )
                            : (
                                <Copy className="size-3.5" />
                              )}
                          <span className="font-mono font-medium">{currentCount.toLocaleString()}</span>
                          <span>{showNoLicense ? 'domains' : 'rows'}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Click to copy count</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <div className="relative w-56">
                    <Input
                      type="search"
                      placeholder="Search domains..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pl-8 text-xs"
                    />
                    <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contextual banners for selected statuses */}
      {!isLoading && !showNoLicense && selectedStatuses.map((status) => {
        const banner = STATUS_BANNERS[status];
        if (!banner)
          return null;
        return (
          <Card key={status} className={cn('mb-4', banner.cardClass)}>
            <CardContent className="py-3">
              <p className={cn('text-sm', banner.textClass)}>
                <strong>{banner.title}</strong>
                {' '}
                {banner.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
      {!isLoading && showNoLicense && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="py-3">
            <p className="text-sm text-amber-800">
              <strong>No License:</strong>
              {' '}
              These non-deleted domains do not have any license on file. Consider adding appropriate licenses.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Data Grid */}
      {!isLoading && !showNoLicense && (
        <AGDataGrid
          columnDefs={fdicColumnDefs}
          rowData={filteredRows}
          quickFilterText={searchQuery}
          onRowDoubleClicked={handleRowDoubleClick}
          onGridReady={handleGridReady}
          overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No domains found for the selected filters</span>'
        />
      )}
      {!isLoading && showNoLicense && (
        noLicenseData === undefined
          ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Loader2 className="size-5 animate-spin" />
                    <span>Loading domains without licenses...</span>
                  </div>
                </CardContent>
              </Card>
            )
          : (
              <AGDataGrid
                columnDefs={noLicenseColumnDefs}
                rowData={domainsWithoutLicense}
                quickFilterText={searchQuery}
                onRowDoubleClicked={(event) => {
                  if (event.data) {
                    navigate(`/app/domains/${event.data.domain}#licenses`);
                  }
                }}
                overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">All domains have licenses</span>'
              />
            )
      )}

      {/* Task Creation Dialog */}
      {taskDialogData && (
        <CreateTaskDialog
          open={taskDialogOpen}
          onOpenChange={setTaskDialogOpen}
          domain={taskDialogData.domain}
          status={taskDialogData.status}
          context="fdic"
        />
      )}
    </Container>
  );
}

export default FdicMonitorPage;
